import { createSignal, For, Show, type JSX } from 'solid-js'
import type { StatusBandsScope } from 'mormaii-web-presentation/status-bands'
import Css from './status-bands.module.scss'
import { Button } from '../filters'
import { Indicator } from './indicator'

/**
 * O editor de faixas de status.
 *
 * Desenhado em DOIS niveis da cadeia: em "Meus dados" edita as faixas da empresa (ou
 * os padroes da plataforma, para o super admin), e no detalhe do colaborador edita as
 * desta pessoa. Qual nivel esta sendo editado e decisao do presenter hospedeiro;
 * aqui so se desenha o que chegou.
 *
 * ## A densidade, que e o assunto desta tela
 *
 * Sao ate sete indicadores com quatro faixas cada - 56 campos. O painel antigo dava
 * a cada indicador um cartao de largura inteira com as faixas empilhadas, e a tela
 * media 3.292px de altura: quem editava o ultimo indicador nao via mais o botao de
 * salvar.
 *
 * O arranjo aqui e o mesmo do renderizador React, que resolveu isso primeiro e
 * resolveu melhor do que a minha primeira tentativa: cada indicador e um CARTAO numa
 * grade que acompanha a largura, e cada faixa e UMA LINHA - ponto colorido, nivel,
 * minimo, maximo. Seis blocos cabem em duas fileiras de tres, e cada campo continua
 * largo o bastante para quatro digitos. A versao anterior daqui punha um indicador
 * por linha, com as quatro faixas lado a lado, e espremia os oito campos de cada
 * linha ate caberem.
 *
 * ## O contador de revisao nao e necessario aqui
 *
 * O scope traz `revision` porque no React os campos sao nao controlados - ver o
 * comentario em `filters/text-filter.tsx`. Em Solid o campo e controlado e o "Cancelar" limpa a
 * tela sozinho.
 */
export function StatusBands(props: { scope: StatusBandsScope }): JSX.Element {
    /*
        `collapsed` guarda so a INTENCAO do leitor, e `open` a deriva.

        Inicializar o sinal com `!props.scope.collapsible` nao funciona: o sinal e
        criado uma vez, na montagem, e nesse instante o presenter pode ainda nao ter
        escrito `collapsible` - o editor do colaborador abria expandido, que e o
        contrario do que ele pede. Derivando, a decisao acompanha o scope.
    */
    const [collapsed, setCollapsed] = createSignal(true)
    const open = () => !props.scope.collapsible || !collapsed()

    const submit = (event: Event) => {
        event.preventDefault()
        void props.scope.onSave()
    }

    /*
        `onReset` do proprio formulario, e nao um `onClick`.

        E o que faz o "Cancelar" ser um botao de RESET de verdade: o navegador o trata
        como tal, e o gesto fica igual ao de qualquer formulario da plataforma.
    */
    const reset = (event: Event) => {
        event.preventDefault()
        void props.scope.onReset()
    }

    return (
        <form class={Css.editor} onSubmit={submit} onReset={reset} aria-busy={props.scope.busy}>
            <header class={Css.header}>
                <div class={Css.texts}>
                    <h2 class={Css.title}>{props.scope.title}</h2>

                    {/*
                        Duas linhas, e elas respondem perguntas diferentes.

                        A primeira diz QUEM estes limites alcancam - ela nao muda
                        enquanto se edita. A segunda diz DE ONDE vem o que esta na
                        tela agora, e muda ao salvar ou ao voltar a herdar. Junta-las
                        numa so faria a parte fixa piscar junto com a movel.
                    */}
                    <Show when={props.scope.appliesTo}>{(texto) => <p class={Css.scopeLine}>{texto()}</p>}</Show>

                    <Show when={originLine(props.scope)}>{(line) => <p class={Css.sourceLine}>{line()}</p>}</Show>
                </div>

                <Show when={props.scope.collapsible}>
                    <button type="button" onClick={() => setCollapsed(!collapsed())} class={Css.expand}>
                        {open() ? 'Recolher' : 'Expandir'}
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            class={Css.expandArrow}
                            classList={{ [Css.collapsed]: !open() }}
                        >
                            <path d="m18 15-6-6-6 6" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </button>
                </Show>
            </header>

            <Show when={open()}>
                <div class={Css.body}>
                    {/*
                        Os avisos de COBERTURA, abertos e por extenso.

                        O icone na faixa diz onde esta o problema; estes blocos dizem
                        quantos sao e quais, porque o icone de um indicador recolhido
                        ou fora da tela nao seria visto. Sao dois porque dizem coisas
                        opostas: a sobreposicao impede salvar, o vao nao - ver
                        `checkCoverage` no presenter. Os textos vem do scope.
                    */}
                    <Show when={props.scope.overlaps.length > 0}>
                        <div class={`${Css.warning} ${Css.blocking}`}>
                            <p class={Css.warningTitle}>{props.scope.overlapTitle}</p>
                            <ul class={Css.warningList}>
                                <For each={props.scope.overlaps}>{(aviso) => <li>{aviso}</li>}</For>
                            </ul>
                            <p class={Css.warningNote}>{props.scope.overlapNote}</p>
                        </div>
                    </Show>

                    <Show when={props.scope.gaps.length > 0}>
                        <div class={Css.warning}>
                            <p class={Css.warningTitle}>{props.scope.gapTitle}</p>
                            <ul class={Css.warningList}>
                                <For each={props.scope.gaps}>{(aviso) => <li>{aviso}</li>}</For>
                            </ul>
                            <p class={Css.warningNote}>{props.scope.gapNote}</p>
                        </div>
                    </Show>

                    {/*
                        Seis blocos de altura igual: em duas colunas cabem em tres
                        fileiras, em tres colunas em duas. O corte e `xl` porque um
                        bloco precisa de ~300px para os dois campos nao ficarem
                        estreitos demais para quatro digitos.
                    */}
                    <div class={Css.metrics}>
                        <For each={props.scope.indicators}>{(indicator) => <Indicator scope={indicator} />}</For>
                    </div>

                    <Show when={props.scope.error}>
                        {(message) => (
                            <p role="alert" class={Css.error}>
                                {message()}
                            </p>
                        )}
                    </Show>

                    <div class={Css.actions}>
                        {/*
                            Dois botoes porque sao DUAS intencoes:

                            "Usar valores padrao" COPIA os padroes para os campos, e
                            quem grava e o Confirmar - serve a quem quer partir deles
                            e ajustar. O colaborador continua com faixa propria.

                            "Voltar a seguir o padrao" APAGA a sobrescrita, e o
                            colaborador volta a herdar - passa a acompanhar quando o
                            padrao mudar. So aparece quando ha o que desfazer.

                            Um rotulo so para as duas prometeria a acao errada a
                            metade de quem clica.
                        */}
                        <Show when={props.scope.canFollowDefaults}>
                            <Button variant="ghost" onClick={() => void props.scope.onFollowDefaults()}>
                                Voltar a seguir o padrão
                            </Button>
                        </Show>

                        <Show when={props.scope.canRestoreDefaults}>
                            <Button variant="ghost" onClick={() => void props.scope.onRestoreDefaults()}>
                                Usar valores padrão
                            </Button>
                        </Show>

                        <Button type="reset" variant="outline" disabled={props.scope.busy}>
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={props.scope.busy}>
                            {props.scope.busy ? 'Salvando…' : 'Confirmar edições'}
                        </Button>
                    </div>
                </div>
            </Show>
        </form>
    )
}

/**
 * Uma linha dizendo de onde vieram os valores que estao na tela.
 *
 * O scope ja carregava `inheritedFrom` e, na primeira versao daqui, so um aviso
 * grande aparecia quando a faixa era herdada - editando faixa PROPRIA a tela nao
 * dizia nada, e era isso que fazia "Voltar a seguir o padrão" aparecer e sumir sem
 * explicacao. Uma linha curta abaixo do titulo diz os tres casos.
 *
 * Nao existe para quem edita os PROPRIOS padroes da plataforma - o super admin -,
 * porque ali nao ha cadeia acima: `canRestoreDefaults` e falso exatamente nesse caso,
 * e e o mesmo motivo por que ele nao ganha "Usar valores padrão".
 */
function originLine(scope: StatusBandsScope): string | undefined {
    if (!scope.canRestoreDefaults) {
        return undefined
    }

    switch (scope.inheritedFrom) {
        /*
         * "Da empresa" passou a ser verdade em 17/09/2026.
         *
         * O degrau do meio era por USUARIO - cada administrador tinha os seus, e
         * dizer "da empresa" afirmava um compartilhamento que nao existia. Hoje ele
         * e da empresa mesmo, e a cadeia e a da pessoa medida.
         */
        case 'COMPANY':
            return 'Seguindo os limites da empresa'
        case 'DEFAULT':
            return 'Seguindo os limites padrão da plataforma'
        default:
            return 'Limites próprios, definidos aqui'
    }
}
